import * as v from 'valibot';
import type { DataSnapshot, Query, Reference } from '../RefBuilder';
import { RefBuilder } from '../RefBuilder';

interface CreateParams<Json, Entity, PathParam> {
  name: string;
  schema: v.BaseSchema<Json, Entity, v.BaseIssue<unknown>>;
  toJson: (value: Entity) => Json;
  buildPath?: (params: PathParam) => string;
  queryPath?: string; // クエリを実行するためのパス
}

// ts-unused-exports:disable-next-line
export interface EntityRef<Json, Entity> {
  get(): Promise<Entity | null>;
  set(value: Entity): Promise<void>;
  update(value: Entity, keys: (keyof Json)[]): Promise<void>;
  remove(): Promise<void>;
  reference: Reference;
}

// EntityRepositoryのエンティティの型を推論するための型
// ts-unused-exports:disable-next-line
export type InferEntity<Repository extends EntityRepository<unknown, unknown, unknown>> = v.InferOutput<
  Repository['schema']
>;

/**
 * RTDB上のデータを型安全なエンティティとして扱うためのリポジトリクラス
 */
export class EntityRepository<Json, Entity, PathParam = string> {
  private constructor(
    public readonly schema: v.BaseSchema<Json, Entity, v.BaseIssue<unknown>>,
    public readonly toJson: (value: Entity) => Json,
    public readonly buildPath: (params: PathParam) => string = (params: PathParam) => '' + params,
    public readonly queryPath: string = ''
  ) {}

  /**
   * EntityRepositoryのインスタンスを作成する
   * @param params.schema valibotのスキーマを指定する
   * @param params.toJson valibotでパースした結果できるエンティティをRTDB上のJSONに変換する関数を指定する。変換したJSONの値にundefinedが含まれているとエラーになるのでnullに自動的に変換される
   * @param params.name EntityRepositoryの名前を指定する
   * @param params.buildPath エンティティのパスを生成する関数を指定する。省略した場合はpathParamをそのまま文字列に変換したものがパスになる
   * @param params.queryPath クエリを実行するためのパスを指定する。省略した場合は''になる
   */
  static create<Json, Entity, PathParam = string>(
    params: CreateParams<Json, Entity, PathParam>
  ): EntityRepository<Json, Entity, PathParam> {
    const Repository = class extends EntityRepository<Json, Entity, PathParam> {};
    // デバッグ時にわかりやすくするためクラス名を振る
    Object.defineProperty(Repository, 'name', { value: params.name });

    return new Repository(params.schema, params.toJson, params.buildPath, params.queryPath ?? '');
  }

  /**
   * エンティティを取得する。対象のスナップショットがない場合はnullを返す
   */
  async get(pathParam: PathParam): Promise<Entity | null> {
    try {
      const snapshot = await RefBuilder.ref(this.buildPath(pathParam)).get();
      if (snapshot.exists()) {
        return v.parse(this.schema, snapshot.val());
      } else {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // 権限エラーの場合には null を返す。それ以外の例外の場合は、例外をスローする。
      if ('message' in e && e.message === 'Permission denied') {
        console.warn('Permission denied for path:', this.buildPath(pathParam), e);
        return null;
      } else {
        throw e;
      }
    }
  }

  /**
   * エンティティを取得する。対象のスナップショットがない場合は例外を投げる
   */
  async getOrThrow(pathParam: PathParam): Promise<Entity> {
    const snapshot = await RefBuilder.ref(this.buildPath(pathParam)).get();
    if (snapshot.exists()) {
      return v.parse(this.schema, snapshot.val());
    }

    throw new Error(`Entity not found at path: ${this.buildPath(pathParam)}`);
  }

  private onCallback: ((snapshot: DataSnapshot) => void) | null = null;

  /**
   * 指定したパスのエンティティを継続的に監視する
   */
  on(pathParam: PathParam, callback: (value: Entity | null) => void, onError?: (error: Error) => void) {
    this.onCallback = (snapshot) => {
      if (snapshot.exists()) {
        callback(v.parse(this.schema, snapshot.val()));
      } else {
        callback(null);
      }
    };

    if (this.onCallback) {
      return RefBuilder.ref(this.buildPath(pathParam)).on('value', this.onCallback, onError);
    }
  }

  /**
   * 指定したパスのエンティティの継続的監視を解除する
   */
  off(pathParam: PathParam): void {
    if (this.onCallback) {
      RefBuilder.ref(this.buildPath(pathParam)).off('value', this.onCallback);
      this.onCallback = null;
    }
  }

  /**
   * エンティティをRTDB上のJSONに変換して書き込む
   */
  async set(pathParam: PathParam, value: Entity): Promise<void> {
    const json = this.removeUndefinedField(this.toJson(value));
    return RefBuilder.ref(this.buildPath(pathParam)).set(json);
  }

  /**
   * RTDB上に書き込むためのオブジェクトを生成する
   */
  getUpdate(pathParam: PathParam, entity: Entity | null): Record<string, unknown> {
    return { [this.buildPath(pathParam)]: entity === null ? null : this.removeUndefinedField(this.toJson(entity)) };
  }

  /**
   * エンティティをRTDB上のJSONに変換して一部のプロパティを書き込む
   * @param pathParam
   * @param value
   * @param keys 更新する対象のプロパティ名の配列を指定する
   */
  async update(pathParam: PathParam, value: Entity, keys: (keyof Json)[]): Promise<void> {
    const path = this.buildPath(pathParam);
    const json = this.toJson(value);
    const values = Object.fromEntries(keys.map((key) => [key, this.removeUndefinedField(json[key])]));
    return RefBuilder.ref(path).update(values);
  }

  /**
   * RTDBのデータを削除する
   */
  async remove(pathParam: PathParam) {
    return RefBuilder.ref(this.buildPath(pathParam)).remove();
  }

  ref(pathParam: PathParam): EntityRef<Json, Entity> {
    return {
      get: this.get.bind(this, pathParam),
      set: this.set.bind(this, pathParam),
      update: this.update.bind(this, pathParam),
      remove: this.remove.bind(this, pathParam),
      reference: RefBuilder.ref(this.buildPath(pathParam)),
    };
  }

  /**
   * 指定したベースパスに対してRealtime DatabaseのQueryを実行し、子要素をエンティティとして返す
   * @param buildQuery refからQueryを構築する関数（orderByChild や startAt などを指定）
   */
  async query(buildQuery: (ref: Reference) => Query): Promise<Array<{ key: string; value: Entity }>> {
    if (this.queryPath === '') {
      throw Error('queryPath is not set. Cannot execute query.');
    }

    const ref = RefBuilder.ref(this.queryPath);
    const snapshot = await buildQuery(ref).get();
    const result: Array<{ key: string; value: Entity }> = [];

    snapshot.forEach((child) => {
      const key = child.key as string;
      const value = v.parse(this.schema, child.val());
      result.push({ key, value });
    });

    return result;
  }

  // RTDBに保存する際にフィールドにundefinedが含まれているとエラーになるので、undefinedが含まれていたらnullに変換する
  private removeUndefinedField(value: unknown): unknown {
    if (value === undefined) {
      return null;
    }

    if (typeof value === 'object') {
      if (value === null) {
        return null;
      }

      if (Array.isArray(value)) {
        return value.map((item) => this.removeUndefinedField(item));
      }

      return Object.fromEntries(
        Object.entries({ ...value }).map(([key, value]) => [key, this.removeUndefinedField(value)])
      );
    }

    return value;
  }
}
